import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles, categories } from '@/mocks/data';
import { useState } from 'react';

const categoryMap: Record<string, string> = {};
categories.forEach((c) => {
  categoryMap[c.slug] = c.name;
});

function renderMarkdown(md: string): string {
  const lines = md.split('\n');
  const result: string[] = [];
  let inList: string | null = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.trim() === '') {
      if (inList) {
        result.push(inList === 'ul' ? '</ul>' : '</ol>');
        inList = null;
      }
      continue;
    }

    // Headings
    if (line.startsWith('### ')) {
      if (inList) { result.push(inList === 'ul' ? '</ul>' : '</ol>'); inList = null; }
      result.push(`<h3 class="text-lg font-heading font-semibold text-foreground-950 mt-8 mb-3">${processInline(line.slice(4))}</h3>`);
      continue;
    }
    if (line.startsWith('## ')) {
      if (inList) { result.push(inList === 'ul' ? '</ul>' : '</ol>'); inList = null; }
      result.push(`<h2 class="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-10 mb-4">${processInline(line.slice(3))}</h2>`);
      continue;
    }
    if (line.startsWith('# ')) {
      if (inList) { result.push(inList === 'ul' ? '</ul>' : '</ol>'); inList = null; }
      result.push(`<h1 class="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-10 mb-4">${processInline(line.slice(2))}</h1>`);
      continue;
    }

    // Unordered list
    if (line.match(/^- /)) {
      if (inList !== 'ul') {
        if (inList) result.push(inList === 'ul' ? '</ul>' : '</ol>');
        result.push('<ul class="list-disc pl-5 space-y-1 mb-4">');
        inList = 'ul';
      }
      result.push(`<li class="text-foreground-700 leading-relaxed">${processInline(line.replace(/^- /, ''))}</li>`);
      continue;
    }

    // Ordered list
    if (line.match(/^\d+\. /)) {
      if (inList !== 'ol') {
        if (inList) result.push(inList === 'ul' ? '</ul>' : '</ol>');
        result.push('<ol class="list-decimal pl-5 space-y-1 mb-4">');
        inList = 'ol';
      }
      result.push(`<li class="text-foreground-700 leading-relaxed">${processInline(line.replace(/^\d+\.\s*/, ''))}</li>`);
      continue;
    }

    // Regular paragraph
    if (inList) { result.push(inList === 'ul' ? '</ul>' : '</ol>'); inList = null; }
    result.push(`<p class="text-foreground-700 leading-relaxed mb-4">${processInline(line)}</p>`);
  }

  if (inList) { result.push(inList === 'ul' ? '</ul>' : '</ol>'); }

  return result.join('\n');
}

function processInline(text: string): string {
  let result = text;
  result = result.replace(/\*\*(.+?)\*\*/g, '<strong class="text-foreground-900 font-semibold">$1</strong>');
  result = result.replace(/`([^`]+)`/g, '<code class="bg-background-100 px-1.5 py-0.5 rounded text-sm text-primary-600 font-mono">$1</code>');
  result = result.replace(/❌/g, '<span class="text-red-500">❌</span>');
  result = result.replace(/✅/g, '<span class="text-green-500">✅</span>');
  return result;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">文章未找到</h1>
          <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryName = categoryMap[article.category] || article.category;
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const contentHtml = renderMarkdown(article.content);

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('订阅成功！感谢您的关注。');
      return;
    }
    setFormStatus('loading');
    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9tm8053pcjqs2fcsgdg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('订阅成功！感谢您的关注。');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormStatus('error');
        setFormMessage(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://lijinye.cn/article/${article.slug}`}
        ogType="article"
        ogImage={article.imageUrl}
      />
      <SchemaOrg type="Article" data={{ headline: article.title, description: article.excerpt, image: article.imageUrl, author: { '@type': 'Person', name: article.author }, datePublished: article.date, publisher: { '@type': 'Organization', name: '学生的妈妈' } }} />
      <SchemaOrg type="BreadcrumbList" data={{ itemListElement: [{ '@type': 'ListItem', position: 1, name: '首页', item: 'https://lijinye.cn' }, { '@type': 'ListItem', position: 2, name: categoryName, item: `https://lijinye.cn/category/${article.category}` }, { '@type': 'ListItem', position: 3, name: article.title }] }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <article className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <header className="mt-8 mb-10">
              <Link
                to={`/category/${article.category}`}
                className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-600 hover:bg-primary-100 transition-colors cursor-pointer mb-4"
              >
                {categoryName}
              </Link>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight">
                {article.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-line"></i>
                  </span>
                  {article.author}
                  <span className="text-foreground-400">· {article.role}</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line"></i>
                  </span>
                  阅读约 {article.readTime} 分钟
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line"></i>
                  </span>
                  {article.date}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 rounded text-xs bg-secondary-100 text-secondary-700">
                    #{tag}
                  </span>
                ))}
              </div>
            </header>

            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={`${article.title} - 学生的妈妈`}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="article-content text-base"
              dangerouslySetInnerHTML={{ __html: contentHtml }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-foreground-500">觉得这篇文章有帮助？</p>
                  <p className="text-sm font-medium text-foreground-800">分享给更多妈妈吧</p>
                </div>
                <div className="flex gap-2">
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer" aria-label="分享到微信">
                    <i className="ri-wechat-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer" aria-label="分享到微博">
                    <i className="ri-weibo-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer" aria-label="复制链接">
                    <i className="ri-link"></i>
                  </button>
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 md:px-6 mt-16">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard
                    key={ra.slug}
                    slug={ra.slug}
                    title={ra.title}
                    category={ra.category}
                    excerpt={ra.excerpt}
                    imageUrl={ra.imageUrl}
                    author={ra.author}
                    readTime={ra.readTime}
                    date={ra.date}
                    tags={ra.tags}
                    categoryName={categoryMap[ra.category]}
                    categorySlug={ra.category}
                  />
                ))}
              </div>
            </section>
          )}

          <section className="max-w-4xl mx-auto px-4 md:px-6 mt-16">
            <div className="bg-accent-500 rounded-lg p-8 text-center">
              <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-50/25 text-background-50">
                <i className="ri-mail-send-line text-xl"></i>
              </span>
              <h3 className="mt-4 text-xl font-heading font-bold text-background-50">
                喜欢这篇文章？订阅获取更多
              </h3>
              <p className="mt-2 text-sm text-background-100">
                每周精选家庭教育干货，直送您的邮箱
              </p>
              <form
                onSubmit={handleSubscribe}
                data-readdy-form
                className="mt-5 flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="输入您的邮箱地址"
                  required
                  className="flex-1 px-4 py-2.5 rounded-lg bg-background-50 text-foreground-800 placeholder:text-foreground-400 text-sm outline-none"
                />
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="website-alt-field"
                />
                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="px-5 py-2.5 rounded-lg bg-foreground-950 text-background-50 font-medium text-sm hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {formStatus === 'loading' ? '提交中...' : '立即订阅'}
                </button>
              </form>
              {formStatus === 'success' && (
                <p className="mt-3 text-sm text-background-50 font-medium">{formMessage}</p>
              )}
              {formStatus === 'error' && (
                <p className="mt-3 text-sm text-background-50 font-medium">{formMessage}</p>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}